import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useIsDesktop from '../../hooks/useIsDesktop.js';

/**
 * Bottom sheet on mobile, centered dialog on desktop.
 *
 * Portalled to <body>: on desktop the switcher renders inside .sidebar, whose
 * position:sticky opens a stacking context — the backdrop and panel were then
 * trapped under any positioned element in .app-main (the sticky .home-charts
 * rail painted straight over them).
 *
 * Mobile uses plain DOM + one-shot CSS slide (no motion transforms).
 * Framer `y` / opacity on the panel caused a sheet-sized black flash on iOS
 * when the native receipt file picker returned and React re-rendered.
 */
// Several sheets can stack (a confirm over a form), so the page scroll lock is
// counted rather than toggled.
let openSheets = 0;

export default function Sheet({ open, onClose, title, children, dismissible = true }) {
    // 768, not the shell's 1024 — a centred dialog already reads correctly on a
    // tablet, and widening this line would regress that.
    const desktop = useIsDesktop(768);
    const ignoreBackdropUntil = useRef(0);
    const panelRef = useRef(null);
    const requestCloseRef = useRef(onClose);
    const onCloseRef = useRef(onClose);
    const dragRef = useRef(null);
    const [closing, setClosing] = useState(false);
    const titleId = useId();
    const { t } = useTranslation('common');

    // Mobile sheets slide out before the parent unmounts them. Desktop dialogs,
    // and anyone who asked for reduced motion, close at once.
    const requestClose = () => {
        const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (desktop || reduce) onClose();
        else setClosing(true);
    };
    useEffect(() => { requestCloseRef.current = requestClose; onCloseRef.current = onClose; });

    // Backstop: if animationend never fires (background tab), still close.
    useEffect(() => {
        if (!closing) return undefined;
        const timer = setTimeout(() => { setClosing(false); onCloseRef.current?.(); }, 600);
        return () => clearTimeout(timer);
    }, [closing]);

    useEffect(() => {
        if (!open) return undefined;
        openSheets += 1;
        document.body.classList.add('has-sheet');
        return () => {
            openSheets -= 1;
            if (openSheets <= 0) { openSheets = 0; document.body.classList.remove('has-sheet'); }
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const mark = () => { ignoreBackdropUntil.current = Date.now() + 800; };
        window.addEventListener('focus', mark);
        document.addEventListener('visibilitychange', mark);
        return () => {
            window.removeEventListener('focus', mark);
            document.removeEventListener('visibilitychange', mark);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const previouslyFocused = document.activeElement;
        const panel = panelRef.current;
        const focusables = () => Array.from(panel?.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []).filter((element) => element.getClientRects().length > 0);
        const initial = panel?.querySelector('[data-sheet-autofocus]') || focusables()[0] || panel;
        initial?.focus();

        const handleKeyDown = (event) => {
            const dialogs = Array.from(document.querySelectorAll('.sheet[role="dialog"][aria-modal="true"]'));
            if (dialogs[dialogs.length - 1] !== panel) return;
            if (event.key === 'Escape' && dismissible && !event.defaultPrevented) {
                event.preventDefault();
                event.stopPropagation();
                requestCloseRef.current?.();
                return;
            }
            if (event.key !== 'Tab' || !panel) return;
            const items = focusables();
            if (!items.length) {
                event.preventDefault();
                panel.focus();
                return;
            }
            const first = items[0];
            const last = items[items.length - 1];
            if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
                requestAnimationFrame(() => previouslyFocused.focus());
            }
        };
    }, [open, dismissible]);

    if (!open) return null;

    const handleBackdropClick = () => {
        if (!dismissible) return;
        if (Date.now() < ignoreBackdropUntil.current) return;
        requestClose();
    };

    // Drag-to-dismiss from the grab zone only, so it never fights the body's own
    // vertical scroll. The offset goes straight to a CSS variable (no React
    // state), which keeps the iOS black-flash re-render path out of it.
    const dragStart = (event) => {
        if (!dismissible || desktop || event.target.closest('button')) return;
        dragRef.current = { y0: event.clientY, t0: performance.now(), dy: 0 };
        event.currentTarget.setPointerCapture?.(event.pointerId);
        panelRef.current?.classList.add('is-dragging');
    };
    const dragMove = (event) => {
        const drag = dragRef.current;
        if (!drag) return;
        drag.dy = Math.max(0, event.clientY - drag.y0);
        panelRef.current?.style.setProperty('--sheet-dy', `${drag.dy}px`);
    };
    const dragEnd = () => {
        const drag = dragRef.current;
        dragRef.current = null;
        const panel = panelRef.current;
        if (!drag || !panel) return;
        panel.classList.remove('is-dragging');
        const velocity = drag.dy / Math.max(1, performance.now() - drag.t0);
        if (drag.dy > 96 || velocity > 0.6) {
            panel.style.setProperty('--sheet-dy', '100%');
            requestClose();
        } else {
            panel.style.removeProperty('--sheet-dy');
        }
    };
    const handlePanelAnimationEnd = (event) => {
        if (!closing || event.target !== event.currentTarget || event.animationName !== 'ms-sheet-out') return;
        setClosing(false);
        onClose();
    };

    const header = (
        <div className="sheet-grab" onPointerDown={dragStart} onPointerMove={dragMove} onPointerUp={dragEnd} onPointerCancel={dragEnd}>
            <div className="sheet-handle" />
            <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 id={titleId} className="fs-5 fw-bold m-0">{title}</h2>
            {dismissible && (
                <button
                    type="button"
                    className="icon-btn"
                    data-sheet-close="true"
                    onClick={requestClose}
                    aria-label={t('close')}
                >
                    <i className="bi bi-x-lg" />
                </button>
            )}
            </div>
        </div>
    );

    return createPortal(
        <>
            <div className={`sheet-backdrop${closing ? ' is-closing' : ''}`} onClick={handleBackdropClick} />
            {desktop ? (
                <div className="sheet-desktop-wrap">
                    <div ref={panelRef} className="sheet sheet-desktop" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex="-1">
                        {header}
                        {children}
                    </div>
                </div>
            ) : (
                <div
                    ref={panelRef}
                    className={`sheet sheet-mobile-enter${closing ? ' is-closing' : ''}`}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={titleId}
                    tabIndex="-1"
                    onAnimationEnd={handlePanelAnimationEnd}
                >
                    {header}
                    {children}
                </div>
            )}
        </>,
        document.body,
    );
}
